import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";
import { ApiError } from "../api/errors.ts";
import type { components } from "../api/schema";
import { useApiClient } from "../session/SessionProvider.tsx";

export type Device = components["schemas"]["DeviceResponse"];

export const devicesQueryKey = ["devices"] as const;
export const deviceRuntimeQueryKey = (deviceId: string) => ["devices", deviceId, "runtime"] as const;
export type DeviceRuntime = components["schemas"]["DeviceRuntimeResponse"];

export function useDeviceRuntime(deviceId: string, enabled = true) {
  const client = useApiClient();
  return useQuery({
    queryKey: deviceRuntimeQueryKey(deviceId), enabled, refetchInterval: 5000,
    queryFn: async () => {
      const { data, error, response } = await client.GET("/v1/devices/{deviceId}/runtime", { params: { path: { deviceId } } });
      if (!data) throw ApiError.from(response, error);
      return data;
    },
  });
}

/** Как у backend: `name` в DeviceClaimRequest и DeviceUpdateRequest — max_length=128. */
export const DEVICE_NAME_MAX_LENGTH = 128;

/** E-va пользователя: свои и те, где он гость. */
export function useDevices() {
  const client = useApiClient();
  return useQuery({
    queryKey: devicesQueryKey,
    queryFn: async () => {
      const { data, error, response } = await client.GET("/v1/devices");
      if (!data) throw ApiError.from(response, error);
      return data.devices;
    },
  });
}

/** Переименовать E-va (`PATCH /v1/devices/{id}`, только владелец). */
export function useRenameDevice(deviceId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => {
      const { data, error, response } = await client.PATCH("/v1/devices/{deviceId}", {
        params: { path: { deviceId } },
        body: { name },
      });
      if (!data) throw ApiError.from(response, error);
      return data;
    },
    onSuccess: (device) => replaceDevice(queryClient, device),
  });
}

type DisplayUpdate = components["schemas"]["DeviceDisplayUpdateRequest"];

/**
 * Настройки экрана E-va (`PATCH /v1/devices/{id}/display`, только владелец). Запросы одной E-va идут по очереди:
 * иначе ответ на прежнее положение ползунка мог бы прийти последним и перетереть в кэше свежее.
 */
export function useUpdateDisplay(deviceId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    scope: { id: `device-display:${deviceId}` },
    mutationFn: async (body: DisplayUpdate) => {
      const { data, error, response } = await client.PATCH("/v1/devices/{deviceId}/display", {
        params: { path: { deviceId } },
        body,
      });
      if (!data) throw ApiError.from(response, error);
      return data;
    },
    onSuccess: (device) => replaceDevice(queryClient, device),
  });
}

/** Ответ backend'а заменяет E-va в списке: шапка и таб «E-va» видят изменение сразу. */
function replaceDevice(queryClient: QueryClient, device: Device): void {
  updateCachedDevice(queryClient, device.id, () => device);
}

/** Поправить E-va в списке кэша без запроса к backend, например online-статус из App WS. */
export function updateCachedDevice(queryClient: QueryClient, deviceId: string, update: (device: Device) => Device): void {
  queryClient.setQueryData<Device[]>(devicesQueryKey, (devices) =>
    devices?.map((item) => (item.id === deviceId ? update(item) : item)),
  );
}

/** Название для UI: имя из привязки, без него — серийный номер. */
export function deviceName(device: Device): string {
  return device.name?.trim() || device.serialNumber;
}

/** Подпись в списках: у чужой E-va — с именем владельца, «Кухня • Мария»; у своей — только название. */
export function deviceLabel(device: Device, userId: string | undefined): string {
  const owner = device.ownerId !== userId ? device.ownerName?.trim() : undefined;
  return owner ? `${deviceName(device)} • ${owner}` : deviceName(device);
}

/** На связи с backend: `busy` — тоже подключена, просто занята командой. */
export function isDeviceOnline(device: Device): boolean {
  return device.onlineStatus === "online" || device.onlineStatus === "busy";
}

/** Подпись online-статуса, как в макетах: `online` / `offline`. */
export function deviceStatusLabel(device: Device): string {
  return isDeviceOnline(device) ? "online" : "offline";
}

// Яркость в API — `displayBrightness` 0–255, в интерфейсе — проценты.
export function brightnessToPercent(brightness: number): number {
  return Math.round((brightness / 255) * 100);
}

export function percentToBrightness(percent: number): number {
  return Math.round((percent / 100) * 255);
}

// Выбор активной E-va живёт в браузере. Хранилище бывает недоступно (приватный режим, запрет сайта,
// переполнение): тогда выбор держится в памяти до перезагрузки.
const STORAGE_KEY = "eva.activeDeviceId";
const listeners = new Set<() => void>();
let unsavedId: string | null = null;

function readStoredId(): string | null {
  if (unsavedId !== null) return unsavedId;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Сделать E-va активной, например сразу после привязки. */
export function setActiveDeviceId(id: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, id);
    unsavedId = null;
  } catch {
    unsavedId = id;
  }
  notify();
}

/** Забыть выбор активной E-va, например при выходе из аккаунта. */
export function forgetActiveDevice(): void {
  unsavedId = null;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Хранилище недоступно: в нём и так ничего не сохранено.
  }
  notify();
}

function notify(): void {
  for (const listener of [...listeners]) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Состояние записи истории при переходе на главную сразу после привязки: главная один раз показывает 07.02
 * и сразу стирает флаг из записи, чтобы ни «назад», ни перезагрузка его не вернули.
 */
export const JUST_CLAIMED_STATE = { justClaimed: true } as const;

export function isJustClaimed(state: unknown): boolean {
  return typeof state === "object" && state !== null && "justClaimed" in state && state.justClaimed === true;
}

export type ActiveDevice = {
  /** `undefined` — список ещё не загружен. */
  devices: Device[] | undefined;
  /** Сохранённая E-va, а если её больше нет в списке — первая доступная; `null` — E-va нет. */
  active: Device | null;
  setActive: (deviceId: string) => void;
};

/** Активная E-va из шапки: её показывает таб «E-va», она адресат по умолчанию в compose. */
export function useActiveDevice(): ActiveDevice {
  const { data: devices } = useDevices();
  const storedId = useSyncExternalStore(subscribe, readStoredId);
  const active = devices?.find((item) => item.id === storedId) ?? devices?.[0] ?? null;
  return { devices, active, setActive: setActiveDeviceId };
}
